import { SseParsedEvent } from "./sse/sseParser";
import { useRef } from "react";
import { useChatAPI } from "./hooks/useChatAPI";
import { streamMessagesFromResponse } from "./sse/messageStreamer";
import useLogger from "../../Logger/useLogger";

export interface SendMessageOptions {
  handleMessageEvent: (
    event: SseParsedEvent,
    abortSignal: AbortController,
    chat_uuid: string
  ) => void;
}

export interface UseChatStreamReturnType {
  sendMessage: (chat_uuid: string, message: string) => Promise<AbortController>;
  stopStreaming: () => void;
}

const useChatStream = ({
  handleMessageEvent,
}: SendMessageOptions): UseChatStreamReturnType => {
  const abortControllerRef = useRef<AbortController | null>(null);
  const { sendMessage: sendMessageAndGetStream } = useChatAPI();
  const { logError } = useLogger();

  const sendMessage = async (
    chat_uuid: string,
    message: string
  ): Promise<AbortController> => {
    let stream: Awaited<ReturnType<typeof sendMessageAndGetStream>>;
    try {
      stream = await sendMessageAndGetStream(chat_uuid, message);
    } catch {
      // The underlying error (network failure, SSE TTFB timeout, etc.) has
      // already been logged with appropriate Sentry tags by useFetchClient.
      // Here we just translate it into the SSE "error" event so the UI shows
      // the error state and the retry option — preserving the existing flow.
      const abortController = new AbortController();
      abortController.abort();
      handleMessageEvent(
        {
          event: "error",
          data: "Error sending message",
        },
        abortController,
        chat_uuid
      );
      return abortController;
    }
    const { reader, abortController } = stream;
    abortControllerRef.current = abortController;
    streamMessagesFromResponse({
      reader,
      onEvent: (event) =>
        handleMessageEvent(event, abortControllerRef.current!, chat_uuid),
      abortSignal: abortControllerRef.current!.signal,
    })
      .catch((error) => {
        logError(error, "Error streaming messages in useChatStream");
      })
      .finally(() => {
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      });
    return abortController;
  };

  const stopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  return {
    sendMessage,
    stopStreaming,
  };
};

export default useChatStream;
